import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ApartmentRounded,
  DragIndicatorRounded,
  GroupsRounded,
  PersonRounded,
  SearchRounded,
  UnfoldLessRounded,
  UnfoldMoreRounded,
} from '@mui/icons-material';

import type {
  StacOrgEditorUnit,
  StacOrgEditorWorker,
} from '../types/stac';


export type StacOrgSelection =
  | {
    kind: 'unit';
    id: string;
  }
  | {
    kind: 'worker';
    id: string;
  };


type ActiveDrag =
  | {
    kind: 'unit';
    unitId: string;
  }
  | {
    kind: 'worker';
    workerId: string;
  };


interface PendingUnitMove {
  unitId: string;
  targetUnitId: string;
}


interface StacOrganizationTreeWorkspaceProps {
  rootUnit: StacOrgEditorUnit;

  unitById: Map<
    string,
    StacOrgEditorUnit
  >;

  workerById: Map<
    string,
    StacOrgEditorWorker
  >;

  childrenByParent: Map<
    string,
    StacOrgEditorUnit[]
  >;

  workersByUnit: Map<
    string,
    StacOrgEditorWorker[]
  >;

  expanded: Set<string>;
  selection: StacOrgSelection | null;

  search: string;
  trayWorkers: StacOrgEditorWorker[];
  unassignedWorkerCount: number;

  busy: boolean;

  onSearchChange: (
    value: string
  ) => void;

  onLocateWorker: (
    worker: StacOrgEditorWorker
  ) => void;

  onSelect: (
    selection: StacOrgSelection
  ) => void;

  onToggle: (
    unitId: string
  ) => void;

  onExpandAll: () => void;
  onCollapseAll: () => void;

  onMoveWorker: (
    workerId: string,
    targetUnitId: string | null,
  ) => void;

  onMoveUnit: (
    unitId: string,
    targetUnitId: string,
  ) => void;
}


function descendantsOf(
  unitId: string,
  childrenByParent: Map<
    string,
    StacOrgEditorUnit[]
  >,
): Set<string> {
  const result =
    new Set<string>();

  const visit = (
    currentId: string,
  ) => {
    for (
      const child
      of childrenByParent.get(
        currentId
      )
      ?? []
    ) {
      if (
        result.has(
          child.id
        )
      ) {
        continue;
      }

      result.add(
        child.id
      );

      visit(
        child.id
      );
    }
  };

  visit(
    unitId
  );

  return result;
}


function PersonCard({
  worker,
  isManager,
  selected,
  busy,
  compact = false,
  onSelect,
}: {
  worker: StacOrgEditorWorker;
  isManager: boolean;
  selected: boolean;
  busy: boolean;
  compact?: boolean;
  onSelect: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    id:
      `stac-worker:${worker.worker_id}`,
    data: {
      kind: 'worker',
      workerId:
        worker.worker_id,
    } satisfies ActiveDrag,
    disabled: busy,
  });

  return (
    <Paper
      id={`stac-org-worker-${worker.worker_id}`}
      ref={setNodeRef}
      elevation={0}
      onClick={onSelect}
      sx={{
        borderRadius: 2,
        border:
          selected
            ? (
              '1px solid '
              + 'rgba(56,189,248,0.58)'
            )
            : (
              '1px solid '
              + 'rgba(148,163,184,0.10)'
            ),
        bgcolor:
          selected
            ? 'rgba(56,189,248,0.09)'
            : 'rgba(2,6,23,0.34)',
        px:
          compact
            ? 0.75
            : 0.9,
        py:
          compact
            ? 0.55
            : 0.7,
        opacity:
          isDragging
            ? 0.3
            : 1,
        cursor: 'pointer',
        transition:
          '140ms ease',
      }}
    >
      <Stack
        direction="row"
        spacing={0.65}
        alignItems="center"
      >
        <Tooltip title="Arrastrar persona">
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            onClick={(event) =>
              event.stopPropagation()
            }
            sx={{
              width: 24,
              height: 24,
              cursor:
                busy
                  ? 'default'
                  : 'grab',
            }}
          >
            <DragIndicatorRounded
              sx={{
                fontSize: 16,
                color: '#64748b',
              }}
            />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            width:
              compact
                ? 25
                : 29,
            height:
              compact
                ? 25
                : 29,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor:
              isManager
                ? 'rgba(56,189,248,0.14)'
                : 'rgba(148,163,184,0.08)',
            border:
              isManager
                ? (
                  '1px solid '
                  + 'rgba(56,189,248,0.28)'
                )
                : (
                  '1px solid '
                  + 'rgba(148,163,184,0.10)'
                ),
            flexShrink: 0,
          }}
        >
          <PersonRounded
            sx={{
              fontSize:
                compact
                  ? 15
                  : 17,
              color:
                isManager
                  ? '#67e8f9'
                  : '#94a3b8',
            }}
          />
        </Box>

        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            color: '#f8fafc',
            fontWeight:
              isManager
                ? 900
                : 750,
            fontSize:
              compact
                ? '0.76rem'
                : '0.81rem',
          }}
        >
          {worker.display_name}
        </Typography>

        {isManager ? (
          <Chip
            size="small"
            label="Responsable"
            variant="outlined"
            sx={{
              display: {
                xs: 'none',
                sm: 'inline-flex',
              },
              height: 22,
              color: '#7dd3fc',
              '& .MuiChip-label': {
                px: 0.75,
                fontSize: '0.68rem',
              },
            }}
          />
        ) : null}
      </Stack>
    </Paper>
  );
}


function UnitNode({
  unit,
  children,
  directWorkers,
  showPeople,
  isOpen,
  selected,
  busy,
  activeDrag,
  invalidTargetUnitIds,
  onSelect,
  onToggle,
  selectedWorkerId,
  onWorkerSelect,
}: {
  unit: StacOrgEditorUnit;
  children: StacOrgEditorUnit[];
  directWorkers: StacOrgEditorWorker[];
  showPeople: boolean;
  isOpen: boolean;
  selected: boolean;
  busy: boolean;
  activeDrag: ActiveDrag | null;
  invalidTargetUnitIds: Set<string>;
  selectedWorkerId: string | null;
  onSelect: () => void;
  onToggle: () => void;
  onWorkerSelect: (
    worker: StacOrgEditorWorker
  ) => void;
}) {
  const canAcceptDrop =
    !busy
    && !invalidTargetUnitIds.has(
      unit.id
    );

  const {
    setNodeRef:
      setDroppableRef,
    isOver,
  } = useDroppable({
    id:
      `stac-unit-drop:${unit.id}`,
    data: {
      kind: 'unit',
      unitId: unit.id,
    },
    disabled:
      !canAcceptDrop,
  });

  const {
    attributes,
    listeners,
    setNodeRef:
      setDraggableRef,
    isDragging,
  } = useDraggable({
    id:
      `stac-unit:${unit.id}`,
    data: {
      kind: 'unit',
      unitId: unit.id,
    } satisfies ActiveDrag,
    disabled:
      busy
      || unit.is_root,
  });

  const hasChildren =
    children.length > 0;

  const hasVisibleContent =
    hasChildren
    || (
      showPeople
      && directWorkers.length > 0
    );

  const dropLabel =
    activeDrag?.kind
      === 'worker'
      ? (
        unit.is_root
          ? 'Dejar sin ubicar'
          : 'Ubicar aquí'
      )
      : 'Mover rama aquí';

  return (
    <Paper
      ref={(element) => {
        setDroppableRef(
          element
        );

        setDraggableRef(
          element
        );
      }}
      elevation={0}
      onClick={onSelect}
      sx={{
        width: '100%',
        maxWidth:
          unit.is_root
            ? 340
            : 310,
        mx: 'auto',
        borderRadius: 3,
        overflow: 'hidden',
        border:
          isOver
          && canAcceptDrop
            ? (
              '1px solid '
              + 'rgba(34,211,238,0.95)'
            )
            : selected
              ? (
                '1px solid '
                + 'rgba(56,189,248,0.72)'
              )
              : (
                '1px solid '
                + 'rgba(148,163,184,0.15)'
              ),
        bgcolor:
          isOver
          && canAcceptDrop
            ? 'rgba(34,211,238,0.10)'
            : selected
              ? 'rgba(14,165,233,0.10)'
              : 'rgba(15,23,42,0.92)',
        boxShadow:
          isOver
          && canAcceptDrop
            ? (
              '0 0 0 4px '
              + 'rgba(34,211,238,0.07)'
            )
            : selected
              ? (
                '0 14px 38px '
                + 'rgba(2,6,23,0.36)'
              )
              : (
                '0 10px 28px '
                + 'rgba(2,6,23,0.22)'
              ),
        opacity:
          isDragging
            ? 0.3
            : 1,
        transition:
          '160ms ease',
      }}
    >
      <Box
        sx={{
          px: 1.2,
          py: 1.05,
        }}
      >
        <Stack
          direction="row"
          spacing={0.8}
          alignItems="center"
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2.2,
              display: 'grid',
              placeItems: 'center',
              bgcolor:
                unit.is_root
                  ? 'rgba(34,211,238,0.14)'
                  : 'rgba(56,189,248,0.09)',
              border:
                '1px solid '
                + 'rgba(56,189,248,0.20)',
              flexShrink: 0,
            }}
          >
            <ApartmentRounded
              sx={{
                color: '#38bdf8',
                fontSize: 19,
              }}
            />
          </Box>

          <Box
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              noWrap
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                fontSize:
                  unit.is_root
                    ? '0.98rem'
                    : '0.91rem',
              }}
            >
              {unit.name}
            </Typography>

            <Stack
              direction="row"
              spacing={0.55}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
            >
              {unit
                .manager_display_name ? (
                <Typography
                  noWrap
                  sx={{
                    color: '#7dd3fc',
                    fontSize: '0.69rem',
                    fontWeight: 750,
                  }}
                >
                  {
                    unit
                      .manager_display_name
                  }
                </Typography>
              ) : null}

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.68rem',
                }}
              >
                {unit.is_root
                  ? 'Raíz organizativa'
                  : (
                    `${unit.direct_worker_count} pers. · `
                    + `${unit.child_unit_count} sub.`
                  )}
              </Typography>
            </Stack>
          </Box>

          {!unit.is_root ? (
            <Tooltip title="Arrastrar unidad">
              <IconButton
                size="small"
                {...attributes}
                {...listeners}
                onClick={(event) =>
                  event.stopPropagation()
                }
                sx={{
                  width: 27,
                  height: 27,
                  cursor:
                    busy
                      ? 'default'
                      : 'grab',
                }}
              >
                <DragIndicatorRounded
                  sx={{
                    fontSize: 18,
                    color: '#64748b',
                  }}
                />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>

        {isOver
        && canAcceptDrop ? (
          <Box
            sx={{
              mt: 0.85,
              borderRadius: 2,
              border:
                '1px dashed '
                + 'rgba(34,211,238,0.50)',
              bgcolor:
                'rgba(34,211,238,0.06)',
              px: 1,
              py: 0.65,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                color: '#67e8f9',
                fontSize: '0.75rem',
                fontWeight: 850,
              }}
            >
              {dropLabel}
            </Typography>
          </Box>
        ) : null}

        {showPeople
        && isOpen
        && directWorkers.length > 0 ? (
          <>
            <Divider
              sx={{
                my: 0.9,
                borderColor:
                  'rgba(148,163,184,0.10)',
              }}
            />

            <Stack spacing={0.55}>
              {directWorkers.map(
                (worker) => (
                  <PersonCard
                    key={
                      worker.worker_id
                    }
                    worker={worker}
                    isManager={
                      unit
                        .manager_worker_id
                      === worker
                        .worker_id
                    }
                    selected={
                      selectedWorkerId
                      === worker.worker_id
                    }
                    busy={busy}
                    compact
                    onSelect={() =>
                      onWorkerSelect(
                        worker
                      )
                    }
                  />
                ),
              )}
            </Stack>
          </>
        ) : null}
      </Box>

      {hasVisibleContent ? (
        <Button
          fullWidth
          size="small"
          onClick={(event) => {
            event.stopPropagation();

            onToggle();
          }}
          startIcon={
            isOpen
              ? <UnfoldLessRounded />
              : <UnfoldMoreRounded />
          }
          sx={{
            borderRadius: 0,
            borderTop:
              '1px solid '
              + 'rgba(148,163,184,0.09)',
            bgcolor:
              'rgba(2,6,23,0.18)',
            textTransform: 'none',
            fontWeight: 750,
            fontSize: '0.71rem',
          }}
        >
          {isOpen
            ? 'Contraer rama'
            : 'Abrir rama'}
        </Button>
      ) : null}
    </Paper>
  );
}


export default function StacOrganizationMapWorkspace({
  rootUnit,
  unitById,
  workerById,
  childrenByParent,
  workersByUnit,
  expanded,
  selection,
  search,
  trayWorkers,
  unassignedWorkerCount,
  busy,
  onSearchChange,
  onLocateWorker,
  onSelect,
  onToggle,
  onExpandAll,
  onCollapseAll,
  onMoveWorker,
  onMoveUnit,
}: StacOrganizationTreeWorkspaceProps) {
  const sensors = useSensors(
    useSensor(
      PointerSensor,
      {
        activationConstraint: {
          distance: 6,
        },
      },
    ),
  );

  const [
    activeDrag,
    setActiveDrag,
  ] = useState<ActiveDrag | null>(
    null
  );

  const [
    pendingUnitMove,
    setPendingUnitMove,
  ] = useState<
    PendingUnitMove | null
  >(
    null
  );

  const [
    showPeople,
    setShowPeople,
  ] = useState(
    true
  );

  const invalidTargetUnitIds =
    useMemo(() => {
      if (
        activeDrag?.kind
        !== 'unit'
      ) {
        return new Set<string>();
      }

      const descendants =
        descendantsOf(
          activeDrag.unitId,
          childrenByParent,
        );

      descendants.add(
        activeDrag.unitId
      );

      return descendants;
    }, [
      activeDrag,
      childrenByParent,
    ]);

  const normalizedSearch =
    search
      .trim()
      .toLocaleLowerCase('es');

  const activeWorker =
    activeDrag?.kind === 'worker'
      ? workerById.get(
        activeDrag.workerId
      )
        ?? null
      : null;

  const activeUnit =
    activeDrag?.kind === 'unit'
      ? unitById.get(
        activeDrag.unitId
      )
        ?? null
      : null;

  const totalUnits =
    Math.max(
      unitById.size - 1,
      0
    );

  const totalPeople =
    workerById.size;

  const handleDragStart = (
    event: DragStartEvent,
  ) => {
    const data =
      event.active.data.current;

    if (
      data?.kind === 'worker'
      && typeof data.workerId
        === 'string'
    ) {
      setActiveDrag({
        kind: 'worker',
        workerId:
          data.workerId,
      });

      return;
    }

    if (
      data?.kind === 'unit'
      && typeof data.unitId
        === 'string'
    ) {
      setActiveDrag({
        kind: 'unit',
        unitId:
          data.unitId,
      });
    }
  };

  const handleDragEnd = (
    event: DragEndEvent,
  ) => {
    const currentDrag =
      activeDrag;

    setActiveDrag(
      null
    );

    const targetUnitId =
      event.over?.data.current
        ?.unitId;

    if (
      !currentDrag
      || typeof targetUnitId
        !== 'string'
    ) {
      return;
    }

    const targetUnit =
      unitById.get(
        targetUnitId
      );

    if (!targetUnit) {
      return;
    }

    if (
      currentDrag.kind
      === 'worker'
    ) {
      const worker =
        workerById.get(
          currentDrag.workerId
        );

      if (!worker) {
        return;
      }

      const resolvedTarget =
        targetUnit.is_root
          ? null
          : targetUnit.id;

      const currentTarget =
        worker.is_unassigned
          ? null
          : worker.unit_id;

      if (
        currentTarget
        === resolvedTarget
      ) {
        return;
      }

      onMoveWorker(
        worker.worker_id,
        resolvedTarget,
      );

      return;
    }

    if (
      invalidTargetUnitIds.has(
        targetUnit.id
      )
    ) {
      return;
    }

    const unit =
      unitById.get(
        currentDrag.unitId
      );

    if (
      !unit
      || unit.is_root
      || unit.parent_unit_id
        === targetUnit.id
    ) {
      return;
    }

    setPendingUnitMove({
      unitId: unit.id,
      targetUnitId:
        targetUnit.id,
    });
  };

  const renderBranch = (
    unit: StacOrgEditorUnit,
  ): ReactNode => {
    const children =
      childrenByParent.get(
        unit.id
      )
      ?? [];

    const directWorkers =
      workersByUnit.get(
        unit.id
      )
      ?? [];

    const isOpen =
      expanded.has(
        unit.id
      );

    const selected =
      selection?.kind
        === 'unit'
      && selection.id
        === unit.id;

    return (
      <Box
        key={unit.id}
        sx={{
          position: 'relative',
          minWidth: 0,
        }}
      >
        <UnitNode
          unit={unit}
          children={children}
          directWorkers={
            directWorkers
          }
          showPeople={
            showPeople
          }
          isOpen={
            isOpen
          }
          selected={
            selected
          }
          busy={busy}
          activeDrag={
            activeDrag
          }
          invalidTargetUnitIds={
            invalidTargetUnitIds
          }
          selectedWorkerId={
            selection?.kind === 'worker'
              ? selection.id
              : null
          }
          onSelect={() =>
            onSelect({
              kind: 'unit',
              id: unit.id,
            })
          }
          onToggle={() =>
            onToggle(
              unit.id
            )
          }
          onWorkerSelect={(
            worker
          ) =>
            onSelect({
              kind: 'worker',
              id:
                worker.worker_id,
            })
          }
        />

        {isOpen
        && children.length > 0 ? (
          <Box
            sx={{
              position: 'relative',
              mt: 3.4,
              pt: 2.2,
              '&::before': {
                content: '""',
                position:
                  'absolute',
                top: 0,
                left: '50%',
                width: 1,
                height: 22,
                bgcolor:
                  'rgba(56,189,248,0.25)',
              },
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md:
                    `repeat(${
                      Math.min(
                        children.length,
                        3
                      )
                    }, minmax(230px, 1fr))`,
                },
                gap: 2,
                alignItems: 'start',
                position: 'relative',
                '&::before':
                  children.length > 1
                    ? {
                      content: '""',
                      position:
                        'absolute',
                      top: 0,
                      left:
                        '12.5%',
                      right:
                        '12.5%',
                      height: 1,
                      bgcolor:
                        'rgba(56,189,248,0.20)',
                    }
                    : undefined,
              }}
            >
              {children.map(
                (child) => (
                  <Box
                    key={child.id}
                    sx={{
                      position:
                        'relative',
                      pt: 2.2,
                      '&::before': {
                        content:
                          '""',
                        position:
                          'absolute',
                        top: 0,
                        left: '50%',
                        width: 1,
                        height: 18,
                        bgcolor:
                          'rgba(56,189,248,0.20)',
                      },
                    }}
                  >
                    {renderBranch(
                      child
                    )}
                  </Box>
                ),
              )}
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  };

  const pendingUnit =
    pendingUnitMove
      ? unitById.get(
        pendingUnitMove.unitId
      )
        ?? null
      : null;

  const pendingTarget =
    pendingUnitMove
      ? unitById.get(
        pendingUnitMove
          .targetUnitId
      )
        ?? null
      : null;

  const trayVisible =
    unassignedWorkerCount > 0
    || normalizedSearch.length > 0;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={
          closestCenter
        }
        onDragStart={
          handleDragStart
        }
        onDragCancel={() =>
          setActiveDrag(
            null
          )
        }
        onDragEnd={
          handleDragEnd
        }
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3.5,
            border:
              '1px solid '
              + 'rgba(148,163,184,0.15)',
            bgcolor:
              'rgba(9,15,29,0.88)',
            overflow: 'hidden',
            minHeight: 620,
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.5,
                md: 2,
              },
              py: 1.45,
              borderBottom:
                '1px solid '
                + 'rgba(148,163,184,0.11)',
              bgcolor:
                'rgba(15,23,42,0.72)',
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                xl: 'row',
              }}
              spacing={1.2}
              justifyContent=
                "space-between"
              alignItems={{
                xs: 'stretch',
                xl: 'center',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2.5,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor:
                      'rgba(34,211,238,0.10)',
                    border:
                      '1px solid '
                      + 'rgba(34,211,238,0.18)',
                  }}
                >
                  <GroupsRounded
                    sx={{
                      color: '#22d3ee',
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 950,
                      fontSize: '1rem',
                    }}
                  >
                    Mapa organizativo
                  </Typography>

                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.76rem',
                    }}
                  >
                    Explora, selecciona y
                    reorganiza la estructura.
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    ml: 0.5,
                  }}
                >
                  <Chip
                    size="small"
                    label={
                      `${totalUnits} unidades`
                    }
                  />

                  <Chip
                    size="small"
                    label={
                      `${totalPeople} personas`
                    }
                  />
                </Stack>
              </Stack>

              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                spacing={0.8}
                alignItems={{
                  xs: 'stretch',
                  sm: 'center',
                }}
              >
                <TextField
                  size="small"
                  placeholder="Buscar persona"
                  value={search}
                  onChange={(event) =>
                    onSearchChange(
                      event.target.value
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                      >
                        <SearchRounded
                          sx={{
                            color:
                              '#64748b',
                            fontSize: 18,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: {
                      xs: '100%',
                      sm: 230,
                    },
                  }}
                />

                <Stack
                  direction="row"
                  spacing={0.3}
                  alignItems="center"
                >
                  <Switch
                    size="small"
                    checked={
                      showPeople
                    }
                    onChange={(
                      _event,
                      checked
                    ) =>
                      setShowPeople(
                        checked
                      )
                    }
                  />

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.74rem',
                      whiteSpace:
                        'nowrap',
                    }}
                  >
                    Personas
                  </Typography>
                </Stack>

                <Tooltip
                  title="Expandir todo"
                >
                  <IconButton
                    onClick={
                      onExpandAll
                    }
                  >
                    <UnfoldMoreRounded />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title="Contraer todo"
                >
                  <IconButton
                    onClick={
                      onCollapseAll
                    }
                  >
                    <UnfoldLessRounded />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          {trayVisible ? (
            <Box
              sx={{
                px: {
                  xs: 1.5,
                  md: 2,
                },
                py: 1.2,
                borderBottom:
                  '1px solid '
                  + 'rgba(148,163,184,0.10)',
                bgcolor:
                  'rgba(2,6,23,0.24)',
              }}
            >
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.74rem',
                    fontWeight: 800,
                    mr: 0.5,
                  }}
                >
                  {normalizedSearch
                    ? 'Resultados'
                    : 'Sin ubicar'}
                </Typography>

                {trayWorkers.map(
                  (worker) => (
                    <Box
                      key={
                        worker.worker_id
                      }
                      sx={{
                        width: 210,
                      }}
                    >
                      <PersonCard
                        worker={worker}
                        isManager={false}
                        selected={
                          selection?.kind
                            === 'worker'
                          && selection.id
                            === worker
                              .worker_id
                        }
                        busy={busy}
                        compact
                        onSelect={() =>
                          onLocateWorker(
                            worker
                          )
                        }
                      />
                    </Box>
                  ),
                )}

                {!trayWorkers.length ? (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.76rem',
                    }}
                  >
                    No hay resultados.
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          ) : null}

          <Box
            sx={{
              px: {
                xs: 1,
                md: 2,
              },
              py: 2.2,
              overflowX: 'auto',
              minHeight: 520,
              background:
                'radial-gradient('
                + 'circle at 50% 0%, '
                + 'rgba(14,165,233,0.055), '
                + 'transparent 36%)',
            }}
          >
            <Box
              sx={{
                minWidth: {
                  xs: 0,
                  md: 680,
                },
                width: '100%',
              }}
            >
              {renderBranch(
                rootUnit
              )}
            </Box>
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop:
                '1px solid '
                + 'rgba(148,163,184,0.09)',
              bgcolor:
                'rgba(2,6,23,0.20)',
            }}
          >
            <Typography
              sx={{
                color: '#64748b',
                fontSize: '0.7rem',
                textAlign: 'center',
              }}
            >
              Arrastra una persona sobre una
              unidad para reubicarla. Arrastra
              una unidad para mover toda su rama.
            </Typography>
          </Box>
        </Paper>

        <DragOverlay>
          {activeWorker ? (
            <Paper
              elevation={8}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2.5,
                bgcolor: '#172033',
                border:
                  '1px solid '
                  + 'rgba(56,189,248,0.40)',
                minWidth: 220,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <PersonRounded
                  sx={{
                    color: '#38bdf8',
                  }}
                />

                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  {
                    activeWorker
                      .display_name
                  }
                </Typography>
              </Stack>
            </Paper>
          ) : activeUnit ? (
            <Paper
              elevation={8}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2.5,
                bgcolor: '#172033',
                border:
                  '1px solid '
                  + 'rgba(56,189,248,0.40)',
                minWidth: 220,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <ApartmentRounded
                  sx={{
                    color: '#38bdf8',
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    {
                      activeUnit
                        .name
                    }
                  </Typography>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.75rem',
                    }}
                  >
                    Mover rama
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Dialog
        open={
          Boolean(
            pendingUnitMove
          )
        }
        onClose={() =>
          setPendingUnitMove(
            null
          )
        }
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#0f172a',
            backgroundImage:
              'none',
            color: '#f8fafc',
          },
        }}
      >
        <DialogTitle>
          Mover rama
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5}>
            <Typography
              sx={{
                color: '#cbd5e1',
              }}
            >
              Vas a mover
              {' '}
              <strong>
                {
                  pendingUnit
                    ?.name
                }
              </strong>
              {' '}
              y toda su estructura dentro de
              {' '}
              <strong>
                {
                  pendingTarget
                    ?.name
                }
              </strong>
              .
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.8rem',
              }}
            >
              La posición anterior seguirá
              disponible en el histórico.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setPendingUnitMove(
                null
              )
            }
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={
              !pendingUnitMove
              || busy
            }
            onClick={() => {
              if (
                !pendingUnitMove
              ) {
                return;
              }

              onMoveUnit(
                pendingUnitMove
                  .unitId,
                pendingUnitMove
                  .targetUnitId,
              );

              setPendingUnitMove(
                null
              );
            }}
          >
            Mover rama
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
